import React, { useEffect, useState, forwardRef } from "react";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { Calendar } from "lucide-react";
import { toEnglishNumbers, toPersianNumbers } from "../utils/numbers";
import "react-multi-date-picker/styles/backgrounds/bg-dark.css";

interface JalaliDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  minDate?: string;
}

const JalaliDatePicker: React.FC<JalaliDatePickerProps> = ({
  value,
  onChange,
  label,
  placeholder = "انتخاب تاریخ",
  required = false,
  minDate,
}) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Initial check
    setIsDark(document.documentElement.classList.contains("dark"));

    // Set up observer for class changes on HTML
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          setIsDark(document.documentElement.classList.contains("dark"));
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  const handleDateChange = (date: any) => {
    if (date) {
      // DateObject formats based on locale, so it produces Persian digits.
      // We convert them to English digits for consistent storage and filtering.
      const dateStr = date.format("YYYY/MM/DD");
      onChange(toEnglishNumbers(dateStr));
    } else {
      onChange("");
    }
  };

  // Custom Input Component to match app styling
  const CustomInput = forwardRef(
    ({ openCalendar, value, handleValueChange }: any, ref: any) => {
      // Convert the value (which is stored in English digits) to Persian for Display only
      const displayValue = toPersianNumbers(value);
      return (
        <div className="relative" onClick={openCalendar}>
          <input
            ref={ref}
            type="text"
            readOnly
            value={displayValue}
            onChange={handleValueChange}
            placeholder={placeholder}
            required={required}
            className="text-center block w-full pl-10 pr-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none dark:text-white transition-colors cursor-pointer"
          />
          <Calendar
            className="absolute left-3 top-2.5 text-slate-400 pointer-events-none"
            size={18}
          />
        </div>
      );
    },
  );

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {label}
        </label>
      )}
      <DatePicker
        value={value}
        onChange={handleDateChange}
        calendar={persian}
        locale={persian_fa}
        format="YYYY/MM/DD"
        render={<CustomInput />}
        containerClassName="w-full"
        className={isDark ? "bg-dark" : ""}
        calendarPosition="bottom-right"
        portal
        fixMainPosition
        minDate={
          minDate
            ? new Date(new Date(minDate).setHours(0, 0, 0, 0)).valueOf()
            : undefined
        }
      />
    </div>
  );
};

export default JalaliDatePicker;
